import React, { useState, useEffect, useCallback } from 'react';
import { getPayments, AdminPayment } from '../../lib/adminApi';
import { formatDate } from '../../lib/date';

const PAGE_SIZE = 20;

function AdminPayments() {
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  const [payments, setPayments] = useState<AdminPayment[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const fetchPayments = useCallback(async (cursor?: string) => {
    const isLoadMore = !!cursor;
    if (isLoadMore) setLoadingMore(true);
    else {
      setLoading(true);
      setPayments([]);
    }
    setError('');
    try {
      const result = await getPayments({
        search: search || undefined,
        from: from || undefined,
        to: to || undefined,
        minAmount: minAmount || undefined,
        maxAmount: maxAmount || undefined,
        limit: PAGE_SIZE,
        cursor: cursor || undefined,
      });
      if (isLoadMore) {
        setPayments(prev => [...prev, ...result.payments]);
      } else {
        setPayments(result.payments);
      }
      setNextCursor(result.nextCursor);
    } catch {
      setError('Failed to load payments.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [search, from, to, minAmount, maxAmount]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  return (
    <div>
      <div className="admin-topbar">
        <h1>Payments</h1>
      </div>
      <div className="admin-content">
        {/* Filter bar */}
        <div className="admin-filter-bar">
          <div className="admin-filter-group">
            <label className="admin-filter-label">User Search</label>
            <input
              className="admin-filter-input"
              placeholder="Email or name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 220 }}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">From</label>
            <input
              type="date"
              className="admin-filter-input"
              value={from}
              onChange={e => setFrom(e.target.value)}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">To</label>
            <input
              type="date"
              className="admin-filter-input"
              value={to}
              onChange={e => setTo(e.target.value)}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Min Credits</label>
            <input
              className="admin-filter-input"
              placeholder="0"
              value={minAmount}
              onChange={e => setMinAmount(e.target.value)}
              style={{ width: 90 }}
            />
          </div>

          <div className="admin-filter-group">
            <label className="admin-filter-label">Max Credits</label>
            <input
              className="admin-filter-input"
              placeholder="∞"
              value={maxAmount}
              onChange={e => setMaxAmount(e.target.value)}
              style={{ width: 90 }}
            />
          </div>
        </div>

        {error && (
          <div className="alert alert-error" role="alert">{error}</div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <div className="spinner" />
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Amount (credits)</th>
                    <th>Payment ID</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="admin-table-empty">No payments found.</td>
                    </tr>
                  ) : (
                    payments.map(p => (
                      <tr key={p.id}>
                        <td>
                          <div>{p.email}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)' }}>
                            {p.businessName}
                          </div>
                        </td>
                        <td style={{ fontWeight: 600, color: 'var(--accent-dark)' }}>
                          +{p.delta}
                        </td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--text-faint)' }}>
                          {p.referenceId ?? '-'}
                        </td>
                        <td>{formatDate(p.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {nextCursor && (
              <div className="admin-load-more">
                <button
                  className="btn-secondary"
                  onClick={() => fetchPayments(nextCursor)}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Loading…' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminPayments;
